import React from 'react';
import { Box, ButtonBase, CircularProgress, IconButton, InputBase, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import ChatBubbleRounded from '@mui/icons-material/ChatBubbleRounded';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import {
  readChat, sendChat, sendChatFiles, startChat, type ChatLine, type ChatLook,
} from '../../../../services/websiteAssistantService';
import { useWebFont, withFont } from './webFont';

/** Where this browser keeps its chat, so a reload picks it up again. Per chat key. */
const KEPT = (key: string) => `wa-chat:${key}`;

function kept(key: string): { id: string; token: string } | null {
  try {
    const raw = window.sessionStorage.getItem(KEPT(key));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function keep(key: string, value: { id: string; token: string } | null): void {
  try {
    if (value) window.sessionStorage.setItem(KEPT(key), JSON.stringify(value));
    else window.sessionStorage.removeItem(KEPT(key));
  } catch {
    // A browser that keeps nothing still chats; a reload just starts again.
  }
}

/** What the chat shows while it works out a reply, a word at a time. */
const THINKING = ['Thinking…', 'Working it out…', 'Checking…', 'Almost there…'];

function Thinking() {
  const [at, setAt] = React.useState(0);
  React.useEffect(() => {
    const timer = window.setInterval(() => setAt((i) => (i + 1) % THINKING.length), 1500);
    return () => window.clearInterval(timer);
  }, []);
  return <>{THINKING[at]}</>;
}

/**
 * The chat a visitor opens from the corner of the agency's website.
 *
 * It speaks for the agency: its name and colour, never ours, and nothing in it
 * says AI unless the visitor asks (see services/website_assistant/chat.py).
 * The buttons under a message are the answers it offers; anything can be typed
 * instead. A document goes in with the paperclip and is read while they wait.
 */
export default function ChatWidget({ chatKey, look, open, onOpenChange, startWith, onStarted }: {
  chatKey: string; look: ChatLook; open: boolean; onOpenChange: (open: boolean) => void;
  /** A button on the page that opens the chat on one of its own buttons: "quote". */
  startWith?: string | null;
  onStarted?: () => void;
}) {
  const theme = useTheme();
  // The agency's main colour: header, buttons, the visitor's own messages.
  const accent = look.accent_color || look.brand_color;
  const onAccent = theme.palette.getContrastText(accent);
  const font = withFont(useWebFont(look.font, theme.typography.fontFamily));
  const [session, setSession] = React.useState<{ id: string; token: string } | null>(() => kept(chatKey));
  const [lines, setLines] = React.useState<ChatLine[]>([]);
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState<null | 'typing' | 'reading' | 'reading-many'>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  // A file dragged over the chat: the window says it can be dropped there.
  const [dragging, setDragging] = React.useState(false);
  const files = React.useRef<HTMLInputElement>(null);
  const end = React.useRef<HTMLDivElement>(null);
  const box = React.useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const bubble = React.useRef<HTMLButtonElement>(null);
  const wasOpen = React.useRef(open);

  // Focus follows the chat: into the message box once it is open and ready (the box is
  // disabled until the chat has started), back to the bubble when it closes, so a keyboard
  // or screen-reader user is never left behind it.
  // Ready once the chat is started or its history is back: until then nothing can be sent,
  // so a message typed during a reload can't cut the history short.
  const ready = Boolean(session) && lines.length > 0;
  React.useEffect(() => {
    if (open && ready) box.current?.focus();
  }, [open, ready]);
  React.useEffect(() => {
    if (!open && wasOpen.current) bubble.current?.focus();
    wasOpen.current = open;
  }, [open]);

  // Opened: pick up this browser's chat, or start one with the greeting.
  React.useEffect(() => {
    if (!open || lines.length) return;
    let live = true;
    (async () => {
      try {
        if (session) {
          const turn = await readChat(chatKey, session.id, session.token);
          if (live) setLines(turn.messages);
          return;
        }
      } catch {
        keep(chatKey, null);
      }
      try {
        const started = await startChat(chatKey);
        if (!live) return;
        const next = { id: started.conversation_id, token: started.token };
        keep(chatKey, next);
        setSession(next);
        setLines(started.messages);
      } catch (err) {
        if (live) setProblem(err instanceof Error ? err.message : 'The chat could not start');
      }
    })();
    return () => { live = false; };
  }, [open, chatKey, session, lines.length]);

  React.useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [lines, busy]);

  // Opened from "Get an auto quote" on the page: press the chat's own button for them, once.
  React.useEffect(() => {
    if (!startWith || !session || busy || !lines.length) return;
    const last = lines[lines.length - 1];
    const offered = last.role === 'assistant' ? last.choices.find((c) => c.value === startWith) : undefined;
    onStarted?.();
    if (offered) void send({ choice: offered.value }, offered.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startWith, session, lines.length]);

  const add = (more: ChatLine[]) => setLines((was) => [...was, ...more]);
  const mine = (words: string, file: string | null = null): ChatLine => ({
    id: `me-${Date.now()}`, role: 'visitor', text: words, choices: [], at: new Date().toISOString(), file,
  });

  const send = async (said: { text?: string; choice?: string }, shown: string) => {
    if (!session || !ready || busy) return;
    setProblem(null);
    add([mine(shown)]);
    setBusy('typing');
    try {
      const turn = await sendChat(chatKey, session.id, session.token, said);
      add(turn.messages);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That message did not go through');
    } finally {
      setBusy(null);
    }
  };

  // Several documents go as one turn: shown together, read together, answered once.
  const upload = async (picked: File[]) => {
    if (!session || !ready || busy || !picked.length) return;
    setProblem(null);
    if (picked.some((file) => file.size > look.max_bytes)) {
      setProblem('That file is larger than 10 MB. Could you send a smaller one?');
      return;
    }
    add(picked.map((file) => mine(`Sent ${file.name}`, file.name)));
    setBusy(picked.length > 1 ? 'reading-many' : 'reading');
    try {
      const turn = await sendChatFiles(chatKey, session.id, session.token, picked);
      add(turn.messages);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That file did not go through');
    } finally {
      setBusy(null);
    }
  };

  // Dropped on the chat, or picked with the paperclip: the same either way.
  const dropped = async (list: FileList | null) => {
    setDragging(false);
    const all = Array.from(list ?? []);
    const usable = all.filter((file) => look.accepts.includes(file.type));
    await upload(usable);
    // Said after the rest are sent: an upload clears the last problem as it starts.
    if (usable.length < all.length) setProblem('Please send a PDF, or a photo (JPG, PNG or WebP).');
  };
  const holdsFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

  const typed = () => {
    const words = text.trim();
    if (!words) return;
    setText('');
    void send({ text: words }, words);
  };

  const lastChoices = lines.length && lines[lines.length - 1].role === 'assistant' ? lines[lines.length - 1].choices : [];

  if (!open) {
    return (
      <ButtonBase
        ref={bubble}
        onClick={() => onOpenChange(true)}
        aria-label={`Chat with ${look.agency_name || 'us'}`}
        sx={(t) => ({
          position: 'fixed', right: t.spacing(3), bottom: t.spacing(3), zIndex: 20, width: 60, height: 60,
          borderRadius: '50%', bgcolor: accent, color: onAccent, boxShadow: t.tokens.shadow.lg,
          '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
        })}
      >
        <ChatBubbleRounded />
      </ButtonBase>
    );
  }

  return (
    <Box
      role="dialog"
      aria-label={`Chat with ${look.agency_name || 'us'}`}
      onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); }}
      onDragEnter={(e: React.DragEvent) => { if (holdsFiles(e)) { e.preventDefault(); setDragging(true); } }}
      onDragOver={(e: React.DragEvent) => { if (holdsFiles(e)) e.preventDefault(); }}
      onDragLeave={(e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(e: React.DragEvent) => {
        // Only files are ours: dragged text or a link keeps the browser's own drop, into the box.
        if (!holdsFiles(e)) return;
        e.preventDefault();
        if (ready && !busy) {
          void dropped(e.dataTransfer.files);
        } else {
          setDragging(false);
          setProblem('One moment, the chat is still working. Drop it again in a second.');
        }
      }}
      sx={(t) => ({
        position: 'fixed', zIndex: 20, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        right: { xs: 8, sm: 24 }, bottom: { xs: 8, sm: 24 }, left: { xs: 8, sm: 'auto' },
        width: { sm: 390 }, height: { xs: 'calc(100vh - 16px)', sm: 600 }, maxHeight: 'calc(100vh - 16px)',
        borderRadius: `${t.ds.radius.lg}px`, bgcolor: t.tokens.surface.raised, boxShadow: t.tokens.shadow.lg,
        border: `1px solid ${t.tokens.border.subtle}`,
        ...font,
      })}
    >
      {dragging ? (
        <Stack alignItems="center" justifyContent="center" spacing={1} aria-hidden
          sx={(t) => ({
            position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', textAlign: 'center', px: 3,
            bgcolor: alpha(t.tokens.surface.raised, 0.92), border: `2px dashed ${accent}`,
            borderRadius: `${t.ds.radius.lg}px`, color: t.tokens.text.primary,
          })}>
          <DescriptionOutlined sx={{ color: accent, fontSize: 36 }} />
          <Typography variant="subtitle1" sx={{ color: 'inherit' }}>
            {ready && !busy ? 'Drop your document here' : 'One moment, then drop it again'}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
            Declarations page, licence photo or quote form: PDF, JPG, PNG or WebP, up to 10 MB
          </Typography>
        </Stack>
      ) : null}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.5, bgcolor: accent, color: onAccent }}>
        <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ color: 'inherit' }}>{look.agency_name || 'Chat with us'}</Typography>
          <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.85 }}>We usually reply in a few seconds</Typography>
        </Stack>
        <IconButton aria-label="Close the chat" onClick={() => onOpenChange(false)} sx={{ color: 'inherit' }}>
          <CloseRounded />
        </IconButton>
      </Stack>
      {look.notice ? (
        <Typography variant="caption" sx={(t) => ({
          px: 2, py: 0.75, color: t.tokens.text.secondary, bgcolor: t.tokens.surface.sunken,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
        })}>
          {look.notice}
        </Typography>
      ) : null}

      <Stack spacing={1} sx={(t) => ({ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5, bgcolor: t.tokens.surface.canvas })}>
        {lines.map((line) => (
          <Box key={line.id} sx={{ display: 'flex', justifyContent: line.role === 'visitor' ? 'flex-end' : 'flex-start' }}>
            <Box sx={(t) => ({
              maxWidth: '85%', px: 1.5, py: 1, borderRadius: `${t.ds.radius.md}px`, whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              ...(line.role === 'visitor'
                ? { bgcolor: accent, color: onAccent, borderBottomRightRadius: `${t.ds.radius.xs}px` }
                : { bgcolor: t.tokens.surface.raised, color: t.tokens.text.primary,
                  border: `1px solid ${t.tokens.border.subtle}`, borderBottomLeftRadius: `${t.ds.radius.xs}px` }),
            })}>
              {line.file ? (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <DescriptionOutlined fontSize="small" />
                  <Typography variant="body2" sx={{ color: 'inherit' }}>{line.file}</Typography>
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: 'inherit' }}>{line.text}</Typography>
              )}
            </Box>
          </Box>
        ))}
        {busy ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={(t) => ({ color: t.tokens.text.tertiary, pl: 0.5 })}>
            <CircularProgress size={14} sx={{ color: 'inherit' }} />
            <Typography variant="caption" sx={{ color: 'inherit' }}>
              {busy === 'reading' ? 'Reading your document…' : busy === 'reading-many' ? 'Reading your documents…' : <Thinking />}
            </Typography>
          </Stack>
        ) : null}
        {!busy && lastChoices.length ? (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', pt: 0.5 }}>
            {lastChoices.map((c) => (
              <ButtonBase key={c.value} onClick={() => void send({ choice: c.value }, c.label)}
                sx={(t) => ({
                  px: 1.5, py: 0.75, borderRadius: `${t.ds.radius.full}px`, border: `1px solid ${accent}`,
                  color: t.tokens.text.primary, bgcolor: t.tokens.surface.raised,
                  '&:hover': { bgcolor: alpha(accent, 0.08) },
                  '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
                })}>
                <Typography variant="body2" sx={{ color: 'inherit' }}>{c.label}</Typography>
              </ButtonBase>
            ))}
          </Stack>
        ) : null}
        {problem ? (
          <Typography variant="caption" role="alert" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
        ) : null}
        <div ref={end} />
      </Stack>

      <Stack direction="row" spacing={0.5} alignItems="center"
        sx={(t) => ({ px: 1, py: 1, borderTop: `1px solid ${t.tokens.border.subtle}` })}>
        {/* Several at once, like a drop: a declarations page, a form and licences usually come together. */}
        <input ref={files} type="file" hidden multiple data-testid="chat-file" accept={look.accepts.join(',')}
          onChange={(e) => { const picked = e.target.files; void dropped(picked).finally(() => { e.target.value = ''; }); }} />
        <IconButton aria-label="Attach a document" onClick={() => files.current?.click()} disabled={!ready || !!busy}>
          <AttachFileRounded />
        </IconButton>
        <InputBase
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); typed(); } }}
          placeholder="Type your message"
          multiline
          maxRows={4}
          inputRef={box}
          inputProps={{ 'aria-label': 'Your message', maxLength: 1500 }}
          sx={(t) => ({ flex: 1, px: 1, typography: 'body2', color: t.tokens.text.primary })}
          disabled={!ready}
        />
        <IconButton aria-label="Send" onClick={typed} disabled={!ready || !!busy || !text.trim()}
          sx={{ color: accent }}>
          <SendRounded />
        </IconButton>
      </Stack>
    </Box>
  );
}
